import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarX, PackageOpen, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useStandort } from '@/components/layouts/use-standort';
import { usePermission } from '@/components/rbac/use-permission';
import { api } from '@/lib/api';
import { P } from '@/lib/rbac/permissions.generated';
import {
  billingApi,
  type BillingModel,
  type ContingentMode,
  type LegacyTariffImport,
  type Tariff,
  type TariffInput,
} from '@/lib/api/billing';
import {
  BILLING_MODEL_LABEL,
  CONTINGENT_MODE_LABEL,
  INTERVALS,
  INTERVAL_LABEL,
  PERIOD_LABEL,
  formatIsoDate,
  installmentLabel,
  parseEuroToCents,
  tariffPriceLabel,
  todayIso,
  type BadgeVariant,
} from '@/lib/billing';

const ALL = 'all';
const ALL_STANDORTE = 'global';

interface FormOption {
  id: number;
  name: string;
}

function tariffState(tariff: Tariff, today: string): { label: string; variant: BadgeVariant } {
  if (!tariff.isActive) return { label: 'Inaktiv', variant: 'secondary' };
  if (tariff.validTo && tariff.validTo < today) return { label: 'Beendet', variant: 'secondary' };
  if (tariff.validFrom > today) return { label: `Ab ${formatIsoDate(tariff.validFrom)}`, variant: 'info' };
  return { label: 'Gültig', variant: 'success' };
}

export default function TariffsSettingsPage() {
  const { standorte } = useStandort();
  const canEdit = usePermission(P.standort.unterrichtsformen_und_pakete.pakete_bearbeiten);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [forms, setForms] = useState<FormOption[]>([]);
  const [formFilter, setFormFilter] = useState(ALL);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<LegacyTariffImport | null>(null);
  const today = todayIso();

  useEffect(() => {
    api
      .get<Array<{ id: number; name: string }> | { items?: Array<{ id: number; name: string }> }>(
        '/api/unterrichtsformen',
      )
      .then((response) =>
        setForms(
          (Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [])
            .map((row) => ({ id: Number(row.id), name: String(row.name ?? `#${row.id}`) }))
            .sort((a, b) => a.name.localeCompare(b.name, 'de')),
        ),
      )
      .catch(() => toast.error('Unterrichtsformen konnten nicht geladen werden'));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingApi.listTariffs({
        unterrichtsformId: formFilter === ALL ? undefined : Number(formFilter),
        includeInactive,
      });
      setTariffs(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Tarife konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [formFilter, includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const formName = useMemo(() => new Map(forms.map((form) => [form.id, form.name])), [forms]);
  const standortName = useMemo(() => new Map(standorte.map((s) => [s.id, s.name])), [standorte]);

  // Phase 6: Die Altpakete der Unterrichtsform werden zu Kontingent-Tarifen —
  // Vorkasse und Raten getrennt, weil die Zahlart zum Tarif gehört.
  const startLegacyImport = async () => {
    setImporting(true);
    try {
      setImportPreview(
        await billingApi.importLegacyTariffs({
          unterrichtsformId: formFilter === ALL ? undefined : Number(formFilter),
          dryRun: true,
        }),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Altpakete konnten nicht gelesen werden');
    } finally {
      setImporting(false);
    }
  };

  const applyLegacyImport = async () => {
    setImporting(true);
    try {
      const result = await billingApi.importLegacyTariffs({
        unterrichtsformId: formFilter === ALL ? undefined : Number(formFilter),
      });
      toast.success(
        result.created.length > 0
          ? `${result.created.length} Tarif${result.created.length === 1 ? '' : 'e'} angelegt`
          : 'Keine neuen Tarife — alle Altpakete sind schon übernommen',
      );
      setImportPreview(null);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Übernahme fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  const update = async (tariff: Tariff, input: { validTo?: string; isActive?: boolean }, message: string) => {
    try {
      await billingApi.updateTariff(tariff.id, input);
      toast.success(message);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Änderung fehlgeschlagen');
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Tarife</h1>
        <p className="text-muted-foreground">
          Preise je Unterrichtsform, als Beitrag oder als Kontingent. Für eine Preisänderung einen neuen Tarif mit
          Gültig-ab-Datum anlegen und den alten beenden — laufende Verträge behalten ihren Preis.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <Select value={formFilter} onValueChange={setFormFilter}>
            <SelectTrigger className="w-64" aria-label="Unterrichtsform filtern">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Alle Unterrichtsformen</SelectItem>
              {forms.map((form) => (
                <SelectItem key={form.id} value={String(form.id)}>
                  {form.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} />
            Inaktive zeigen
          </label>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void startLegacyImport()}
              disabled={forms.length === 0 || importing}
            >
              <PackageOpen className="mr-2 h-4 w-4" />
              {importing ? 'Prüft …' : 'Aus Altpaketen übernehmen'}
            </Button>
            <Button onClick={() => setCreating(true)} disabled={forms.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              Tarif anlegen
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <LoadingState message="Tarife werden geladen …" />
      ) : tariffs.length === 0 ? (
        <EmptyState message="Noch keine Tarife. Ohne Tarif gilt das Standardmodell des Mandanten." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarif</TableHead>
                <TableHead>Standort</TableHead>
                <TableHead>Modell</TableHead>
                <TableHead>Preis</TableHead>
                <TableHead>Gültig</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Aktionen</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tariffs.map((tariff) => {
                const state = tariffState(tariff, today);
                const ended = Boolean(tariff.validTo && tariff.validTo < today);
                return (
                  <TableRow key={tariff.id}>
                    <TableCell>
                      <div className="font-medium">
                        {tariff.name}
                        {tariff.sourcePackageId ? (
                          <span
                            className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                            title="Wird automatisch aus den Paketen der Unterrichtsform gepflegt"
                          >
                            aus Paket
                          </span>
                        ) : null}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formName.get(tariff.unterrichtsformId) ?? `Unterrichtsform #${tariff.unterrichtsformId}`}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {tariff.standortId === null
                        ? 'Alle Standorte'
                        : (standortName.get(tariff.standortId) ?? `Standort #${tariff.standortId}`)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {BILLING_MODEL_LABEL[tariff.model]}
                      {tariff.model === 'contingent' && tariff.contingentMode && (
                        <span className="text-muted-foreground"> · {CONTINGENT_MODE_LABEL[tariff.contingentMode]}</span>
                      )}
                      {tariff.model === 'periodic' && tariff.allowedIntervals && (
                        <div className="text-muted-foreground">
                          {tariff.allowedIntervals.map((i) => INTERVAL_LABEL[i]).join(', ')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">{tariffPriceLabel(tariff)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatIsoDate(tariff.validFrom)} – {tariff.validTo ? formatIsoDate(tariff.validTo) : 'offen'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={state.variant} size="sm">
                        {state.label}
                      </Badge>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!ended && tariff.isActive && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void update(tariff, { validTo: today }, 'Tarif endet heute')}
                            >
                              <CalendarX className="mr-2 h-4 w-4" />
                              Beenden
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              void update(
                                tariff,
                                { isActive: !tariff.isActive },
                                tariff.isActive ? 'Tarif deaktiviert' : 'Tarif aktiviert',
                              )
                            }
                          >
                            <Power className="mr-2 h-4 w-4" />
                            {tariff.isActive ? 'Deaktivieren' : 'Aktivieren'}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {importPreview && (
        <Dialog open onOpenChange={(open) => !open && setImportPreview(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Tarife aus Altpaketen übernehmen</DialogTitle>
              <DialogDescription>
                Aus jedem Altpaket wird ein Tarif je Zahlart. Preise werden eingefroren; vorhandene
                Tarife bleiben unberührt.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {importPreview.created.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Alle Altpakete sind bereits als Tarif vorhanden.
                </p>
              ) : (
                importPreview.created.map((plan) => (
                  <div
                    key={`${plan.sourcePackageId}-${plan.name}`}
                    className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm"
                  >
                    <div>
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {formName.get(plan.unterrichtsformId) ?? `Unterrichtsform #${plan.unterrichtsformId}`}
                        {' · '}
                        {CONTINGENT_MODE_LABEL[plan.contingentMode]}
                      </div>
                    </div>
                    <div className="tabular-nums">{tariffPriceLabel(plan)}</div>
                  </div>
                ))
              )}
              {importPreview.skipped.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {importPreview.skipped.length} Paket
                  {importPreview.skipped.length === 1 ? '' : 'e'} bereits übernommen.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportPreview(null)} disabled={importing}>
                Abbrechen
              </Button>
              <Button
                onClick={() => void applyLegacyImport()}
                disabled={importing || importPreview.created.length === 0}
              >
                {importing ? 'Legt an …' : `${importPreview.created.length} Tarife anlegen`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {creating && (
        <TariffDialog
          forms={forms}
          standorte={standorte}
          initialFormId={formFilter === ALL ? undefined : Number(formFilter)}
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

interface TariffDialogProps {
  forms: FormOption[];
  standorte: Array<{ id: number; name: string }>;
  initialFormId?: number;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function TariffDialog({ forms, standorte, initialFormId, onClose, onSaved }: TariffDialogProps) {
  const [formId, setFormId] = useState(initialFormId ? String(initialFormId) : '');
  const [standortId, setStandortId] = useState(ALL_STANDORTE);
  const [name, setName] = useState('');
  const [model, setModel] = useState<BillingModel>('periodic');
  const [price, setPrice] = useState('');
  const [period, setPeriod] = useState('1');
  const [intervals, setIntervals] = useState<number[]>([]);
  const [units, setUnits] = useState('10');
  const [mode, setMode] = useState<ContingentMode>('prepaid');
  const [installments, setInstallments] = useState('6');
  const [validFrom, setValidFrom] = useState(todayIso());
  const [validTo, setValidTo] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const priceCents = parseEuroToCents(price);
    if (!formId) return toast.error('Unterrichtsform wählen');
    if (priceCents === null) return toast.error('Preis als Betrag angeben, z. B. 24,50');
    const input: TariffInput = {
      unterrichtsformId: Number(formId),
      standortId: standortId === ALL_STANDORTE ? null : Number(standortId),
      name: name.trim(),
      model,
      priceCents,
      validFrom,
      validTo: validTo || null,
    };
    if (model === 'periodic') {
      input.pricePeriodMonths = Number(period);
      input.allowedIntervals = intervals.length > 0 ? intervals : null;
    } else {
      const count = Number(units);
      if (!Number.isInteger(count) || count < 1) return toast.error('Anzahl Einheiten muss mindestens 1 sein');
      input.units = count;
      input.contingentMode = mode;
      if (mode === 'installments') {
        const rates = Number(installments);
        if (!Number.isInteger(rates) || rates < 2 || rates > 60) {
          return toast.error('Anzahl Raten zwischen 2 und 60 angeben');
        }
        input.installmentCount = rates;
      }
    }
    setSaving(true);
    try {
      await billingApi.createTariff(input);
      toast.success('Tarif angelegt');
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Tarif konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tarif anlegen</DialogTitle>
          <DialogDescription>
            Preis und Modell sind nach dem Anlegen fest. Neue Verträge übernehmen den am Vertragsbeginn gültigen
            Tarif.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="tariff-form">Unterrichtsform</Label>
            <Select value={formId} onValueChange={setFormId}>
              <SelectTrigger id="tariff-form">
                <SelectValue placeholder="Auswählen" />
              </SelectTrigger>
              <SelectContent>
                {forms.map((form) => (
                  <SelectItem key={form.id} value={String(form.id)}>
                    {form.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tariff-standort">Standort</Label>
            <Select value={standortId} onValueChange={setStandortId}>
              <SelectTrigger id="tariff-standort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STANDORTE}>Alle Standorte</SelectItem>
                {standorte.map((standort) => (
                  <SelectItem key={standort.id} value={String(standort.id)}>
                    {standort.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="tariff-name">Bezeichnung</Label>
            <Input
              id="tariff-name"
              placeholder="z. B. Monatsbeitrag Einzelunterricht 45 min"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tariff-model">Modell</Label>
            <Select value={model} onValueChange={(value) => setModel(value as BillingModel)}>
              <SelectTrigger id="tariff-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BILLING_MODEL_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tariff-price">Preis (€)</Label>
            <Input
              id="tariff-price"
              inputMode="decimal"
              placeholder="0,00"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </div>

          {model === 'periodic' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="tariff-period">Preis gilt je</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger id="tariff-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVALS.map((interval) => (
                      <SelectItem key={interval} value={String(interval)}>
                        {PERIOD_LABEL[interval]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Zahlweisen</legend>
                <div className="flex flex-wrap gap-3">
                  {INTERVALS.map((interval) => (
                    <label key={interval} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={intervals.includes(interval)}
                        onCheckedChange={(checked) =>
                          setIntervals((current) =>
                            checked === true
                              ? [...current, interval].sort((a, b) => a - b)
                              : current.filter((value) => value !== interval),
                          )
                        }
                      />
                      {INTERVAL_LABEL[interval]}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Keine Auswahl: Vorgabe des Mandanten.</p>
              </fieldset>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="tariff-units">Einheiten im Paket</Label>
                <Input
                  id="tariff-units"
                  type="number"
                  min={1}
                  value={units}
                  onChange={(event) => setUnits(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tariff-mode">Abrechnung</Label>
                <Select value={mode} onValueChange={(value) => setMode(value as ContingentMode)}>
                  <SelectTrigger id="tariff-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTINGENT_MODE_LABEL).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {mode === 'installments' && (
                <div className="space-y-2">
                  <Label htmlFor="tariff-installments">Monatsraten</Label>
                  <Input
                    id="tariff-installments"
                    type="number"
                    min={2}
                    max={60}
                    value={installments}
                    onChange={(event) => setInstallments(event.target.value)}
                  />
                  {parseEuroToCents(price) !== null && Number(installments) >= 2 && (
                    <p className="text-xs text-muted-foreground">
                      {installmentLabel(parseEuroToCents(price) ?? 0, Number(installments))}, erste Rate zum
                      Vertragsbeginn. Alle Einheiten stehen sofort zur Verfügung.
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground md:col-span-2">
                {mode === 'usage_based'
                  ? 'Nach Verbrauch: Preis gilt für die angegebene Anzahl Einheiten; abgerechnet wird monatlich nachträglich je verbrauchter Einheit.'
                  : 'Durchgeführte Stunden und unentschuldigtes Fehlen verbrauchen eine Einheit. Ist das Paket aufgebraucht, bucht Sidigo das Folgepaket, solange der Vertrag nicht gekündigt ist.'}
              </p>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="tariff-valid-from">Gültig ab</Label>
            <Input
              id="tariff-valid-from"
              type="date"
              value={validFrom}
              onChange={(event) => setValidFrom(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tariff-valid-to">Gültig bis (optional)</Label>
            <Input
              id="tariff-valid-to"
              type="date"
              value={validTo}
              onChange={(event) => setValidTo(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void save()} disabled={saving || !name.trim() || !validFrom}>
            {saving ? 'Speichert …' : 'Tarif anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
